/**
 * Axios helper 플러그인
 *
 * Vue 컴포넌트에서 axios를 따로 import하지 않고 전역으로 정의된 this.$axios를 사용할 수 있도록 해준다.
 * 사용법은 axios와 동일하다
 *
 * 예: this.$axios.get('url');
 *
 * @type {{install: AxiosHelper.install}}
 */

import axios from 'axios';
import cookie from "@/service/cookieUtil";

class AxiosHelper {
    constructor() { }

    /**
     *
     * @param Vue
     * @param options
     * @returns {Promise<AxiosResponse<any>>}
     */
    install(Vue, options) {
        const axiosConfig = options ? options.axiosConfig : {};
        const includeTokenHeader = options ? !!options.includeTokenHeader : false;
        const axiosInstance = axios.create(axiosConfig);

        axiosInstance.interceptors.response.use(
            response => {
                return response;
            },
            error => {
                const currentUrl = new URL(window.location.href);
                const path = currentUrl.pathname;
                const allowedPaths = [ '/backoffice/login' ]

                if (!allowedPaths.includes(path) && error.response && error.response.status === 401) {
                    location.href = '/backoffice/login';
                }
                return Promise.reject(error);
            }
        );


        function getAccessTokenHeader() {
            if (!includeTokenHeader) return {};
            const accessToken = cookie.get('access_token');

            if (accessToken) {
                return { 'Authorization' : `Bearer ${accessToken}` };
            } else {
                return {};
            }
        }

        Vue.prototype.$axios = {
            get(url, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'get'
                };

                return this.request(_config);
            },

            post(url, data, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'post',
                    data: data
                };

                return this.request(_config);
            },

            put(url, data, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'put',
                    data: data
                };

                return this.request(_config);
            },

            delete(url, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'delete'
                };

                return this.request(_config);
            },

            patch(url, data, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'patch',
                    data: data
                };

                return this.request(_config);
            },

            head(url, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'head'
                };

                return axiosInstance.request(_config);
            },

            options(url, config) {
                const _config = {
                    ... config,
                    url: url,
                    method: 'options'
                };

                return axiosInstance.request(_config);
            },

            async request(config) {
                const configHeaders = config.headers ? config.headers : {};
                const accessTokenHeader = getAccessTokenHeader();
                const headers = {...accessTokenHeader, ...configHeaders};
                const newConfig = {...config, headers: {...headers}};

                try {
                    return await axiosInstance.request(newConfig);
                } catch(err) {
                    if (err.response && err.response.status === 401) {
                        await this.refreshToken();
                        return await axiosInstance.request(newConfig);
                    } else {
                        throw err;
                    }
                }
            },

            async refreshToken() {
                const refreshToken = cookie.get('refresh_token');
                if (!refreshToken) {
                    // throw { message: 'refresh token not found' }
                    return;
                }

                try {
                    const result = await axiosInstance.post('/v1/authenticate', {
                        grantType: 'refresh_token',
                        refreshToken: refreshToken
                    });

                    cookie.set('access_token', result.data.accessToken, '1h');
                    cookie.set('refresh_token', result.data.refreshToken, '2w');
                } catch(err) {
                    cookie.delete('access_token');
                    cookie.delete('refresh_token');
                    throw err;
                }
            }
        }
    }
}

const axiosHelper = new AxiosHelper();

export default axiosHelper;